"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:bg-white/5")}>
              <Link to={item.href || "#"} className="text-sm font-semibold text-white/80 hover:text-primary transition-colors font-sans">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="md:hidden absolute top-full left-0 w-full bg-[#0b121f] border-b border-border/10 shadow-lg animate-in slide-in-from-top-5 fade-in duration-200">
      <nav className="flex flex-col p-6 gap-4">
        {loading ? <div className="flex flex-col gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-6 w-32 bg-muted/50 animate-pulse rounded-md" />)}
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium text-white hover:text-primary transition-colors" onClick={onClose} data-index={i}>
              {item.label}
            </Link>)}
        <div className="pt-4 border-t border-border/10">
          <Button asChild variant="ghost" className="w-full justify-start text-ring hover:text-ring/80 hover:bg-accent/10 px-0">
            <Link to="/ContactPage">Private Consultation</Link>
          </Button>
        </div>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b", isScrolled ? "bg-[#0b121f]/80 backdrop-blur-lg border-border/5 shadow-sm" : "bg-[#0b121f] border-border/10")}>
      <div className="container mx-auto px-4 h-16 md:h-20 flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex-shrink-0 z-50">
          <img src="https://expat-legacy.com/wp-content/uploads/2026/03/Expat-Legacy-WHITE.png" alt="Expat Legacy" className="h-7 w-auto object-contain" />
        </Link>

        {}
        <div className="hidden md:flex flex-1 justify-center px-8">
          <MenuProvider menu_id="17">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          <Button asChild className="hidden md:inline-flex bg-[#c7b08a] text-[#192332] hover:bg-[#c7b08a]/90 font-medium border-none">
            <Link to="/ContactPage">Private Consultation</Link>
          </Button>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-primary hover:bg-white/5" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="17">
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}